import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Input from "@/components/base/Input";
import { caseService, interventionService } from "@/services/api";
import { mapDbCaseToFrontend, mapDbInterventionToFrontend } from "@/services/mapping";

interface ClinicalNote {
  id: string;
  student: string;
  fecha: string;
  nota: string;
  autor: string;
}

export default function PsicologoConfidencialPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Lists
  const [notes, setNotes] = useState<ClinicalNote[]>([]);
  const [cases, setCases] = useState<any[]>([]);

  // Modal notes states
  const [showAddNote, setShowAddNote] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [casesData, interventionsData] = await Promise.all([
        caseService.list(),
        interventionService.list({ tipo: "registro_clinico" })
      ]);

      if (Array.isArray(casesData)) {
        setCases(casesData.map(mapDbCaseToFrontend));
      }
      if (Array.isArray(interventionsData)) {
        setNotes(interventionsData.map((d: any) => ({
          id: d.id,
          student: `${d.estudiante_nombre || "Estudiante"} (${d.caso_folio || "CASO"})`,
          fecha: d.fecha ? new Date(d.fecha).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
          nota: d.descripcion || "",
          autor: d.realizado_por_nombre || "Psicólogo"
        })));
      }
    } catch (err: any) {
      console.error("Error loading clinical vault data:", err);
      setError("Error al conectar con el servidor Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (unlocked) {
      loadData();
    }
  }, [unlocked]);

  const handleUnlock = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === "Ezer2026!") {
      setUnlocked(true);
      setError("");
    } else {
      setError("Código de acceso incorrecto");
    }
  };

  const handleOpenAdd = () => {
    setSelectedCaseId("");
    setNoteText("");
    setShowAddNote(true);
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCaseId) {
      alert("Por favor seleccione un caso.");
      return;
    }
    if (!noteText.trim()) {
      alert("Por favor redacte el contenido de la nota.");
      return;
    }

    setSaving(true);
    try {
      await interventionService.create({
        caso_id: selectedCaseId,
        tipo: "registro_clinico",
        descripcion: noteText.trim(),
        fecha: new Date().toISOString().split("T")[0],
        confidencial: true,
        participantes: [],
        acuerdos: "",
        proximos_pasos: ""
      });
      setShowAddNote(false);
      await loadData();
    } catch (err: any) {
      alert("Error al registrar nota en base de datos: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Registro Clínico Confidencial</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Acceso restringido a notas de terapia y relatos protegidos por RLS en Azure.</p>
      </div>

      {!unlocked ? (
        <div className="max-w-md mx-auto py-12">
          <Card className="text-center space-y-5 border-red-100 bg-red-50/10">
            <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-3xl mx-auto">
              <i className="ri-lock-line" />
            </div>
            <div className="space-y-1.5">
              <h2 className="text-lg font-bold text-foreground-900">Sección Confidencial Protegida</h2>
              <p className="text-xs text-foreground-500 leading-relaxed px-4">
                Esta sección está protegida bajo la **Ley N° 19.628 de Protección de la Vida Privada** y políticas de seguridad PostgreSQL en Azure.
              </p>
            </div>
            <form onSubmit={handleUnlock} className="space-y-4 pt-2">
              <Input
                type="password"
                placeholder="Ingrese su contraseña institucional"
                value={password}
                onChange={e => setPassword(e.target.value)}
                error={error}
                className="text-center"
              />
              <p className="text-3xs text-foreground-400 font-mono">Demo Hint: Ezer2026!</p>
              <Button type="submit" className="w-full bg-red-500 text-white hover:bg-red-600 cursor-pointer">
                Verificar y Desbloquear
              </Button>
            </form>
          </Card>
        </div>
      ) : (
        <div className="space-y-5 animate-fade-in">
          {error && (
            <div className="p-4 bg-red-55 border border-red-200 text-red-800 text-xs rounded-xl font-medium">
              {error}
            </div>
          )}

          <div className="flex bg-red-50 border border-red-200/60 rounded-xl p-4 items-start gap-3">
            <i className="ri-shield-fill text-red-500 text-lg mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-red-800">Sesión de Alta Seguridad Activa</h4>
              <p className="text-xs text-red-700 mt-0.5">Toda consulta, edición y descarga en esta sección queda registrada en el Log de Auditoría Legal de la institución.</p>
            </div>
            <Button size="sm" variant="ghost" className="text-red-700 hover:bg-red-100 flex-shrink-0 ml-auto cursor-pointer" onClick={() => { setUnlocked(false); setPassword(""); }}>
              Cerrar Bóveda
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <Card className="space-y-4">
                <div className="flex items-center justify-between border-b border-background-100 pb-2">
                  <h3 className="font-bold text-foreground-900">Bitácora de Notas Clínicas</h3>
                  <Button size="sm" onClick={handleOpenAdd} className="bg-red-500 text-white hover:bg-red-600 cursor-pointer">
                    <i className="ri-add-line mr-1" /> Nueva Nota
                  </Button>
                </div>
                {loading ? (
                  <div className="flex justify-center items-center py-10">
                    <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-4 divide-y divide-background-100">
                    {notes.map((note) => (
                      <div key={note.id} className="pt-4 first:pt-0 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground-900">{note.student}</span>
                            <span className="text-foreground-400 font-mono">{note.fecha}</span>
                          </div>
                          <Badge variant="danger">{note.autor}</Badge>
                        </div>
                        <p className="text-xs text-foreground-600 leading-relaxed font-sans bg-background-50 p-3 rounded-lg border border-background-100">{note.nota}</p>
                      </div>
                    ))}
                    {notes.length === 0 && (
                      <p className="text-sm text-foreground-400 text-center py-8">No hay notas clínicas registradas.</p>
                    )}
                  </div>
                )}
              </Card>
            </div>
            <Card className="space-y-4 h-fit">
              <h3 className="font-bold text-foreground-900 border-b border-background-100 pb-2">Estudiantes con Registro Crítico</h3>
              <div className="space-y-3">
                {cases.filter(c => c.prioridad === "critica" || c.prioridad === "critico").slice(0, 5).map((s, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-background-50 border border-background-100/50">
                    <div>
                      <p className="text-xs font-semibold text-foreground-800">{s.studentName}</p>
                      <p className="text-3xs text-foreground-400">{s.curso}</p>
                    </div>
                    <Badge variant="danger">Alto</Badge>
                  </div>
                ))}
                {cases.filter(c => c.prioridad === "critica" || c.prioridad === "critico").length === 0 && (
                  <p className="text-xs text-foreground-450 text-center py-4">No hay estudiantes críticos</p>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ── MODAL: NUEVA NOTA CLÍNICA ── */}
      {showAddNote && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowAddNote(false)}>
          <form onSubmit={handleAddSubmit} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Nueva Nota Clínica Confidencial</h2>
              <button type="button" onClick={() => setShowAddNote(false)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Caso Relacionado</label>
                <select
                  value={selectedCaseId}
                  onChange={(e) => setSelectedCaseId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                  required
                >
                  <option value="">Seleccione el caso...</option>
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>{c.studentName} - {c.folio} ({c.titulo.substring(0, 20)}...)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nota de Terapia / Relato Clínico</label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Redacte la evolución de la sesión o antecedentes confidenciales observados..."
                  rows={5}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none text-foreground-850"
                  required
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowAddNote(false)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white cursor-pointer" loading={saving}>
                Guardar Nota
              </Button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}
